/**
 * useUsers / useUser / useCurrentUser — TanStack Query hooks backed by
 * the live `/api/v1/users` API.
 *
 * This file owns the Users surface specifically: user list, user detail,
 * current user, user CRUD mutations, identity fields, status changes,
 * per-user activity / audit / sessions, tenant-wide activity, and bulk
 * user actions (reset-mfa, force-password-reset, access-review, delete,
 * export, import).
 *
 * Sibling per-domain hooks were extracted out of this module per #134
 * to keep ownership boundaries crisp. The full set is re-exported from
 * the bottom of the file so existing callers using `lib/use-users`
 * continue to work without import changes:
 *
 *   - `./use-roles`        — role catalog, role CRUD, role assignments
 *   - `./use-departments`  — department list + CRUD
 *   - `./use-user-groups`  — user group list/detail + CRUD + membership
 *   - `./use-invitations`  — invitation list + resend/revoke
 *
 * Phase C Wave 1a Stage 2 prep step:
 *   - Replaces direct `USERS` constant imports from `mock-users-data.ts`
 *     with hooks that fetch from the API.
 *   - The API surfaces a lean User shape today (~14 fields). The mock
 *     `User` interface has 30+ fields. The `adaptApiUserToUser` adapter
 *     fills missing fields with safe placeholder defaults so existing
 *     components consume the hook output without rewrite.
 *   - Fields marked PLACEHOLDER below are surfaced from the mock
 *     defaults until later Stage 2/3 features add the matching API
 *     endpoints (training, telemetry, sessions, etc.).
 *
 * The adapter's defaults are deliberately conservative:
 *   - clearance.level → 'unclassified', usPerson.verified → false, etc.
 *     (so framework-gated UI stays consistent with the F61 policy resolver).
 *   - role list → derived from API's flat `role` string + caller's
 *     organizationContext (single membership shown in v1; full assign-
 *     ment list lands when W1a-07 ships).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import { normalizeDateOnly } from './format';
// Foundations 2 / B2a-T2 — the wire shape of GET /users/:id/lifecycle and the
// destination union PATCH /users/:id/status accepts, both derived from THE
// transition table. The browser reads the server's PROJECTION of the table and
// holds no transition rule of its own; the one value it imports is
// `normalizeLifecycleState`, which is not a rule but the single resolver for a
// raw `users.status` value — the alternative is the browser keeping a second
// normalizer, which is what review B2aT2-R3 found.
//
// B2c-T3 imports two more VALUES, and both are reads of the table rather than
// rules restated: `LIFECYCLE_STATES` and `LIFECYCLE_TRANSITIONS` are the table
// itself, and `lifecycleDestinationPermissions` below answers "what could an
// edge into this state cost?" from them, so the roster's bulk bar stops
// guessing the price of an action.
import {
  normalizeLifecycleState,
  LIFECYCLE_STATES,
  LIFECYCLE_TRANSITIONS,
  type LifecycleState,
  type LifecycleStatusDestination,
  type UserLifecycleResponse,
} from '@argusai/shared/schemas/user-lifecycle';
import type { RbacPermission } from '@argusai/shared/schemas/permissions';
// Foundations 2 / B2e-T1 — THE identity-activity vocabulary. The rendered-row
// type, the filter alphabet, the dropdown's options and the stats shape all
// come from it, so the browser cannot offer a filter the feed has no action
// for, and cannot compute a number the server answers exactly.
import {
  USER_ACTIVITY_FILTER_TYPES,
  USER_ACTIVITY_STAT_TYPES,
  type UserActivityEventType as SharedUserActivityEventType,
  type UserActivityFilterType,
  type UserActivityStats,
  type UserActivityStatWindow,
} from '@argusai/shared/schemas/user-activity';
import {
  getApiErrorMessage,
  isPermissionDeniedApiError,
  type ApiClientError,
} from './api-error';
import type {
  User,
  UserStatus,
  EmploymentType,
  ClearanceLevel,
  HipaaBaaStatus,
} from './mock-users-data';

// ─── API response shapes ─────────────────────────────────────────────────────

interface ApiUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  avatarUrl: string | null;
  isActive: boolean | null;
  status: string | null;
  isEmailVerified: boolean;
  mfaEnabled: boolean;
  role: string;
  lastLoginAt: string | null;
  createdAt: string;
  // ── Employment fields (bA1 migration, 2026-05-08) ──────────────────────
  // Server returns null for legacy rows that haven't been edited since the
  // migration ran. The adapter forwards them as-is — no placeholder values.
  // B2b-T3 — THE SET, and it is REQUIRED on the wire. The scalar
  // `departmentId` / `departmentName` pair is gone from the API: a person
  // belongs to one or more departments or to all of them, and a pair of
  // optional scalars could name only the first of a set. Not optional, because
  // "the server did not say" is not a state this shape may hold — every user
  // the API returns carries its scope.
  departments: { all: boolean; items: { id: string; name: string }[] };
  position?: string | null;
  managerId?: string | null;
  managerName?: string | null;
  // B2c-T2 — THE employment-type vocabulary, read from the shared schema. It
  // was a four-value union typed out here, while the `User` shape below
  // declared a DIFFERENT four (`fte | contractor | vendor | partner`) and
  // `apiToUiEmploymentType` translated between them lossily.
  employmentType?: EmploymentType | null;
  startDate?: string | null;
  endDate?: string | null;
  costCenter?: string | null;
  employeeId?: string | null;
  /**
   * MAY THE CALLER IMPERSONATE THIS PERSON — the server's answer, from THE rule
   * (B2d-T2, review B2dT2-BF-08). `GET /users/:id` sends it; the list endpoint
   * does not, which is why it is optional here and why the adapter below treats
   * its absence as `false`.
   *
   * The browser must never work this out. The refusals are about the SUBJECT
   * (nobody impersonates an Owner, however the authority was granted — and a
   * tenant role cloned from Owner under another name counts), and re-deriving
   * them here would be the second authority model no-web-derivation-lint exists
   * to keep out.
   */
  canImpersonate?: boolean;
  /**
   * MAY THE CALLER HAND THE TENANT TO THIS PERSON — the server's answer, from
   * THE rule (B2e-T2, `policy/ownership.ts` → `decideOwnershipTransfer`), with
   * the refusal's own words and the phrase the confirmation demands.
   *
   * The browser CANNOT work this out even in principle: the rule asks whether
   * the caller holds the shipped platform Owner ROW, and `permissions[]` never
   * carries `transfer_ownership:tenants` — no grant confers it, so
   * `effectiveGrants` strips it from everybody. Sent only by `GET /users/:id`,
   * which is why it is optional here and why the adapter reads its absence as
   * "not allowed" rather than as an offer.
   */
  ownershipTransfer?: {
    allowed: boolean;
    reason: string | null;
    confirmPhrase: string;
  };
  /**
   * IS THIS PERSON SSO-LINKED (B2d-T4)? Sent by `GET /users/:id`, read from
   * `user_identities` — the existence of a row, not a column somebody set —
   * so the badge cannot disagree with the sign-in path. The LIST endpoint does
   * not send it, which is why it is optional and why the adapter treats its
   * absence as "not linked" rather than inventing an answer.
   */
  sso?: { linked: boolean; providerName: string | null };
  // F61 fields (surfaced when caller has appropriate framework + permission):
  clearance?: ClearanceLevel;
  usPersonVerified?: boolean;
  usPersonVerifiedAt?: string | null;
  usPersonReverifyDue?: string | null;
  hipaaBaaStatus?: HipaaBaaStatus;
  hipaaBaaSignedAt?: string | null;
  hipaaBaaExpiresAt?: string | null;
}

interface ApiUserListResponse {
  data: ApiUser[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

interface ApiUserMeResponse {
  user: ApiUser;
  groups?: Array<{ id: string; name: string; description: string | null }>;
  roles?: Array<{ id: string; name: string }>;
}

// ─── Adapter ─────────────────────────────────────────────────────────────────

/**
 * Hash a string into a stable 0..N color slot. Identical to the mock
 * adapter so existing avatar colors don't change after backend cutover.
 */
function avatarColorFor(seed: string): string {
  const palette = ['blue', 'cyan', 'teal', 'green', 'lime', 'amber', 'orange', 'rose', 'pink', 'fuchsia', 'purple', 'violet'];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

function dormancyDays(lastLoginAt: string | null): number {
  if (!lastLoginAt) return 9999;
  const ms = Date.now() - new Date(lastLoginAt).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

/**
 * Map the API's lean shape into the rich `User` interface the existing UI
 * consumes. Fields not yet served by the API get conservative defaults.
 *
 * NOTE: callers expecting `clearance` / `usPerson` / etc. to reflect real
 * server state must check the framework policy (see useIdentityFieldPolicy)
 * — the API only includes those fields when the tenant has the matching
 * framework active.
 */
export function adaptApiUserToUser(api: ApiUser): User {
  const firstName = api.firstName ?? '';
  const lastName = api.lastName ?? '';
  const fullName = `${firstName} ${lastName}`.trim() || api.email;

  return {
    id: api.id,
    email: api.email,
    firstName,
    lastName,
    fullName,
    employeeId: api.employeeId ?? '',
    phoneNumber: api.phoneNumber,
    // Absent means "the server did not say", which renders as no control —
    // never as an offer (principles.md #4).
    canImpersonate: api.canImpersonate === true,
    // B2e-T2 — same rule, same direction. A response without the key is "the
    // server did not say", which renders as no control; `confirmPhrase` is
    // whatever the server will check, never a name this side assembled.
    ownershipTransfer: {
      allowed: api.ownershipTransfer?.allowed === true,
      reason: api.ownershipTransfer?.reason ?? null,
      confirmPhrase: api.ownershipTransfer?.confirmPhrase ?? '',
    },

    // Real employment fields from the API (bA1 migration).
    //
    // B2b-T3 — THE DEPARTMENTS ARE PASSED THROUGH, NOT COMPUTED. The adapter
    // used to flatten the API's department into two scalars; it now forwards
    // the server's object as it stands, so the roster cell, the detail page
    // field and the filter all render one answer. A response that somehow
    // arrives without the key is "we could not find out", which renders as no
    // departments — never as `all: true`, the fail-open direction.
    departments: {
      all: api.departments?.all === true,
      items: Array.isArray(api.departments?.items) ? api.departments.items : [],
    },
    position: api.position ?? '',
    managerId: api.managerId ?? null,
    managerName: api.managerName ?? null,
    // B2c-T2 — PASSED THROUGH, NOT TRANSLATED. `apiToUiEmploymentType` is
    // deleted: it mapped `part_time` to `fte` and `intern` to `partner`, so
    // the value a screen showed was not the value the store held, and two of
    // the browser's four options could not be stored at all. c32 made the
    // alphabets one list; a row whose column was never set stays null and the
    // screen renders no badge, which is the honest answer.
    employmentType: api.employmentType ?? null,
    // Closes #250: dates flow through `normalizeDateOnly()` so the
    // shape exposed to the UI is ALWAYS YYYY-MM-DD or null. Previously
    // `api.startDate ?? api.createdAt` could leak a full ISO timestamp
    // (the createdAt fallback) into `user.startDate`, and downstream
    // call sites then `slice(0, 10)`'d the timestamp — a behaviour that
    // works in UTC but masks the difference between "DB has a real
    // start_date" and "DB has null". The fallback is removed: when the
    // API returns null, the UI gets null and renders an em-dash, which
    // is the honest answer.
    startDate: normalizeDateOnly(api.startDate),
    endDate: normalizeDateOnly(api.endDate),
    costCenter: api.costCenter ?? null,

    // THE normalizer, not a second one (review B2aT2-R3). This used to be a
    // five-literal fold that defaulted anything else to 'active' — so once the
    // revoke path sets `invited_revoked` (B2c-T2) the roster and the detail
    // header would paint a green "Active" badge on a person the lifecycle
    // endpoint reports as `invited_revoked`, on the same screen. A stored
    // `deleted` folded the same way. `normalizeLifecycleState` is the one
    // resolver for the raw column value; the legacy v0 `inactive` still folds
    // to `active`, there, once.
    status: normalizeLifecycleState(api.status) as UserStatus,
    joinedDate: api.createdAt,
    lastLoginAt: api.lastLoginAt,
    createdAt: api.createdAt,
    updatedAt: api.createdAt,                    // PLACEHOLDER — API doesn't expose updatedAt yet

    mfaEnabled: api.mfaEnabled,
    mfaMethod: api.mfaEnabled ? 'totp' : 'none', // PLACEHOLDER
    mfaEnforcedByPolicy: false,                  // PLACEHOLDER
    passwordLastChangedAt: null,                 // PLACEHOLDER
    passwordExpiresAt: null,                     // PLACEHOLDER
    // B2d-T4 — no longer a PLACEHOLDER. It was hardcoded `false`, so the
    // SSO-linked badge on the user detail page could never render however many
    // people had signed in through an IdP.
    ssoLinked: api.sso?.linked ?? false,
    ssoProvider: api.sso?.providerName ?? null,

    // B1-T4: the role is identified by its KEY — `api.role` itself, which is
    // exactly what `GET /users/roles` now carries on every row and what the
    // server's own filters take. It used to be the sentinel `system:<role>`,
    // a shape the API synthesised in code and no table ever held; the /users
    // list filter matched dropdown value against it, so BOTH had to carry the
    // invented prefix, and `?role=` sent to the server (bulk export) matched
    // nothing at all because the server compares a bare key.
    //
    // History worth keeping: three role-ID shapes (dropdown `system:admin`,
    // adapter `role-admin`, raw compare) once made the role filter drop to
    // zero rows. One vocabulary — the key — is what stops that recurring.
    roles: [{
      roleId: api.role,
      roleName: api.role,
      source: 'direct',
      grantedAt: api.createdAt,
    }],
    groupIds: [],                                // PLACEHOLDER — useUserGroups in Stage 2/3
    activeSessions: [],                          // PLACEHOLDER — useUserSessions in Stage 2
    dormancyDays: dormancyDays(api.lastLoginAt),

    clearance: {
      level: (api.clearance ?? 'unclassified') as ClearanceLevel,
      grantedBy: null,
      grantedAt: api.usPersonVerifiedAt ?? null,
      expiresAt: api.usPersonReverifyDue ?? null,
      caveats: [],
      history: [],
    },
    usPerson: {
      verified: api.usPersonVerified ?? false,
      verifiedBy: null,
      verifiedByName: null,
      verifiedAt: api.usPersonVerifiedAt ?? null,
      documentType: null,
      expiresAt: api.usPersonReverifyDue ?? null,
      notes: null,
    },
    classificationAccess: ['unclassified'],      // PLACEHOLDER
    exportControlAccess: ['none'],               // PLACEHOLDER

    hipaaWorkforceMember: api.hipaaBaaStatus !== undefined && api.hipaaBaaStatus !== 'not_applicable',
    phiAccessLevel: 'none',                      // PLACEHOLDER
    baaOnFile: api.hipaaBaaStatus === 'signed',

    dataRegion: 'US',                            // PLACEHOLDER
    gdprDataSubject: false,                      // PLACEHOLDER

    training: [],                                // PLACEHOLDER
    lastReviewedAt: null,                        // PLACEHOLDER
    lastReviewedBy: null,                        // PLACEHOLDER
    nextReviewDue: null,                         // PLACEHOLDER
    riskScore: 'low',                            // PLACEHOLDER

    telemetry: {                                 // PLACEHOLDER — useUserTelemetry later
      queriesLast7d: 0,
      queriesLast30d: 0,
      avgQueriesPerDay: 0,
      topAgentId: '',
      topAgentName: '',
      documentsAccessedLast30d: 0,
      avgSessionDurationMin: 0,
      lastActive: api.lastLoginAt ?? api.createdAt,
      consumedTokensLast30d: 0,
      estimatedCostLast30d: 0,
    },
    activityLog: [],                             // PLACEHOLDER — useUserActivity in Stage 2
    auditTrail: [],                              // PLACEHOLDER — useUserAudit in Stage 2

    invitation: null,                            // PLACEHOLDER — useUserInvitation in Stage 3

    avatarColor: avatarColorFor(api.email),
  };
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export interface UseUsersFilters {
  search?: string;
  status?: 'all' | UserStatus;
  page?: number;
  pageSize?: number;
  // Closes #73 — server-side department filter. The browser users
  // list previously filtered client-side which broke whenever a
  // newly-created user landed past the default pageSize=20 page
  // boundary. Push the predicate down so the filtered view returns
  // ALL matching users regardless of pagination.
  departmentId?: string;
}

export interface UseUsersResult {
  users: User[];
  pagination?: ApiUserListResponse['pagination'];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

/** One entry of the unprivileged picker — the whole projection `/users/search` returns. */
export interface UserPickerEntry {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

/**
 * THE USER PICKER, for every surface that needs to NAME a colleague rather
 * than administer them: share dialogs, delegation, approver and manager
 * choosers.
 *
 * Reads `GET /users/search`, which is gated by tenant membership alone and
 * returns `{ id, email, firstName, lastName }` capped at 50 — nothing about
 * status, department, manager, MFA or last login.
 *
 * USE THIS AND NOT `useUsers` when the caller is not an admin surface (review
 * R-01 / BF-07, B1-T2b). `useUsers` reads `GET /users`, the ROSTER, which
 * answers `write:users` — the admin audience — so a picker built on it 403s
 * for every Viewer, Member and Power User, react-query swallows the error and
 * the list loads forever. Approvals › Settings shipped exactly that.
 */
export function useUserPicker(search?: string): {
  users: UserPickerEntry[];
  isLoading: boolean;
  error: unknown;
} {
  const params = new URLSearchParams({ pageSize: '50' });
  if (search) params.set('search', search);

  const { data, isLoading, error } = useQuery<{ data: UserPickerEntry[] }>({
    queryKey: ['users', 'search', { search: search ?? '' }],
    queryFn: () =>
      apiClient.get<{ data: UserPickerEntry[] }>(`/users/search?${params.toString()}`),
    staleTime: 30_000,
  });

  return { users: data?.data ?? [], isLoading, error };
}

/**
 * Fetch a paginated list of users from `/api/v1/users` — the ROSTER, and an
 * ADMIN read: the route is gated on `write:users`, so a non-admin caller gets
 * a 403. Picker surfaces want `useUserPicker` above.
 *
 * Filters are passed through; the server applies them when supported,
 * otherwise the caller filters client-side using existing memoized logic.
 */
export function useUsers(filters: UseUsersFilters = {}): UseUsersResult {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  // Closes #73 — only forward a real UUID. Mock/legacy dropdown
  // values (e.g. `dept-engineering`) would 422 the server, so we
  // drop them and fall back to "all".
  if (filters.departmentId
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(filters.departmentId)) {
    params.set('departmentId', filters.departmentId);
  }

  const qs = params.toString();
  const url = qs ? `/users?${qs}` : '/users';

  const { data, isLoading, error, refetch } = useQuery<ApiUserListResponse>({
    queryKey: ['users', filters],
    queryFn: () => apiClient.get<ApiUserListResponse>(url),
    staleTime: 30_000,
  });

  return {
    users: data?.data?.map(adaptApiUserToUser) ?? [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Fetch a single user by id. Returns null while loading or on error
 * (so existing `if (!user) return <NotFound/>` patterns keep working).
 */
export function useUser(id: string | undefined): {
  user: User | null;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery<ApiUser>({
    queryKey: ['users', id],
    queryFn: () => apiClient.get<ApiUser>(`/users/${id}`),
    enabled: !!id,
    staleTime: 30_000,
  });

  return {
    user: data ? adaptApiUserToUser(data) : null,
    isLoading,
    error,
  };
}

/**
 * Fetch the currently-authenticated user. Backed by `/api/v1/users/me`
 * (already wired in Phase B Step 2.5 for the framework-policy hook).
 */
export function useCurrentUser(): {
  user: User | null;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery<ApiUserMeResponse>({
    queryKey: ['users', 'me'],
    queryFn: () => apiClient.get<ApiUserMeResponse>('/users/me'),
    staleTime: 60_000,
  });

  return {
    user: data?.user ? adaptApiUserToUser(data.user) : null,
    isLoading,
    error,
  };
}

/**
 * Invalidate the users cache after a mutation. Call from Create/Edit
 * dialogs after a successful POST/PATCH so the list refetches.
 */
export function useInvalidateUsers() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['users'] });
  };
}

/**
 * Stats summary derived from the loaded user list. The mock data ships
 * a hand-curated `USERS_STATS`; this helper computes the same shape from
 * the live list. Reactive — recomputes when the underlying list changes.
 */
export function useUsersStats(users: User[]): {
  total: number;
  active: number;
  invited: number;
  locked: number;
  suspended: number;
  deactivated: number;
  mfaEnabled: number;
  usPersonVerified: number;
  reviewOverdue: number;
} {
  const now = Date.now();
  let active = 0, invited = 0, locked = 0, suspended = 0, deactivated = 0;
  let mfaEnabled = 0, usPersonVerified = 0, reviewOverdue = 0;
  for (const u of users) {
    switch (u.status) {
      case 'active':      active++;      break;
      case 'invited':     invited++;     break;
      case 'locked':      locked++;      break;
      case 'suspended':   suspended++;   break;
      case 'deactivated': deactivated++; break;
    }
    if (u.mfaEnabled) mfaEnabled++;
    if (u.usPerson.verified) usPersonVerified++;
    if (u.nextReviewDue && new Date(u.nextReviewDue).getTime() < now) reviewOverdue++;
  }
  return { total: users.length, active, invited, locked, suspended, deactivated, mfaEnabled, usPersonVerified, reviewOverdue };
}

// ─── Mutations (used by W1a-03/04/13) ────────────────────────────────────────

/**
 * Body shape for `POST /api/v1/users` — W1a-03 Create User dialog write path.
 *
 * Identity fields (clearance / US-Person / HIPAA BAA) are conditionally
 * present based on the tenant's active compliance framework (resolved via
 * `useIdentityFieldPolicy`). Sending an identity field for a framework
 * that is NOT active returns 400 FRAMEWORK_NOT_ACTIVE — surface those
 * errors back into the dialog's Identity step.
 */
export interface CreateUserBody {
  email: string;
  firstName: string;
  lastName: string;
  role: 'viewer' | 'member' | 'admin';
  employeeId?: string;
  phoneNumber?: string | null;
  // ── Employment fields (bA1 migration) ─────────────────────────────────
  // Optional + nullable. The server validates the department ids / managerId
  // belong to the caller's tenant and that employee_id is unique
  // within the tenant.
  //
  // B2b-T3 — THE SET. Omitting the key creates an ALL-DEPARTMENTS person
  // (decisions.md C7), which is what omitting the old `departmentId` meant.
  // `{ all: false, ids: [] }` is refused by the server with a 400: a person
  // who shares a department with nothing sees only company-wide items, which
  // is never what an admin picked "not all departments" to say.
  departments?: { all: boolean; ids: string[] };
  position?: string | null;
  managerId?: string | null;
  // B2c-T2 — THE one employment-type vocabulary, from the shared schema.
  employmentType?: EmploymentType | null;
  startDate?: string | null;
  endDate?: string | null;
  costCenter?: string | null;
  // Identity fields — gated by framework policy on the server.
  clearance?: ClearanceLevel;
  usPersonVerified?: boolean;
  usPersonVerifiedAt?: string | null;
  hipaaBaaStatus?: HipaaBaaStatus;
  hipaaBaaSignedAt?: string | null;
  hipaaBaaExpiresAt?: string | null;
  // Invitation control. 'send_now' triggers a Pino log on the server
  // (real SMTP integration is captured as F-W1a-03.1). 'manual_link'
  // returns the inviteUrl in the response so the admin can copy it.
  inviteMode?: 'send_now' | 'manual_link';
}

export interface CreateUserResponse {
  user: ApiUser;
  invitation: {
    token: string;
    expiresAt: string;
    inviteUrl: string;
  } | null;
}

/**
 * Create a new user — wires to W1a-03's POST /users.
 *
 * Returns the created `User` (adapted from the API shape). The
 * `invitation` field is populated only when the admin chose
 * `manual_link` mode and explicitly wants to copy the URL — in
 * `send_now` mode it is ALWAYS null and the email channel is the
 * sole bearer of the raw token (closes #256).
 *
 * On success the hook invalidates the `['users']` query so the list
 * refetches and the new user appears immediately.
 */
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<CreateUserResponse, Error, CreateUserBody>({
    mutationFn: (body) =>
      apiClient.post<CreateUserResponse>('/users', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * Body shape for `PATCH /api/v1/users/:id` — W1a-04 Edit User dialog.
 *
 * Generic profile-field PATCH. Identity fields (clearance / US-Person /
 * HIPAA BAA) route through `useUpdateIdentityFields()` (the F61 PATCH
 * /:id/identity-fields endpoint) instead — this body's Zod schema is
 * .strict() and rejects unknown keys with 422 VALIDATION_ERROR.
 *
 * Server enforces:
 *   - empty body         → 422 VALIDATION_ERROR
 *   - sensitive fields   require write:users (role, email)
 *   - non-sensitive      allowed for self-edit even without write:users
 *   - role escalation    rejected (cannot grant role above your own)
 *   - role demote owner  rejected (use a separate flow for owner changes)
 *   - email collision    409 EMAIL_EXISTS
 *   - self role change   403 FORBIDDEN
 *   - self email change  403 FORBIDDEN (security: account-takeover hardening)
 */
export interface UpdateUserBody {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string | null;
  avatarUrl?: string | null;
  role?: 'viewer' | 'member' | 'admin';
  // ── Employment fields (bA1 migration) ─────────────────────────────────
  // Sending null clears the column; omitting the key leaves the existing
  // value untouched. Server enforces tenant boundaries on the FK refs.
  //
  // B2b-T3 — THE SET, and it is not nullable. `departmentId: null` used to
  // mean "clear it", which decisions.md C7 turns into ALL DEPARTMENTS — a
  // widening that reads as a clearing. The screen now says which it means:
  // `{ all: true }` widens, `{ all: false, ids: [...] }` narrows.
  departments?: { all: boolean; ids: string[] };
  position?: string | null;
  managerId?: string | null;
  // B2c-T2 — THE one employment-type vocabulary, from the shared schema.
  employmentType?: EmploymentType | null;
  startDate?: string | null;
  endDate?: string | null;
  costCenter?: string | null;
  employeeId?: string | null;
}

/**
 * Update a user's profile fields — wires to W1a-04's PATCH /users/:id.
 *
 * On success the hook invalidates the `['users']` list query AND the
 * `['users', id]` detail query so both surfaces refetch.
 */
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation<{ user: ApiUser } | ApiUser, Error, {
    userId: string;
    body: UpdateUserBody;
  }>({
    mutationFn: ({ userId, body }) =>
      apiClient.patch(`/users/${userId}`, body),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['users', userId] });
      // A profile change emits a USER_UPDATED audit row.
      qc.invalidateQueries({ queryKey: ['users', userId, 'audit'] });
    },
  });
}

/**
 * Body shape for `PATCH /api/v1/users/:id/identity-fields` (F61).
 *
 * Identity fields are gated by the active compliance framework — sending
 * a field for a framework that's NOT active returns 400
 * FRAMEWORK_NOT_ACTIVE. The Edit dialog already gates fields client-side
 * via `useIdentityFieldPolicy.isVisible(key)` so this should not happen
 * in normal flow; the server check is defense in depth.
 */
export interface UpdateIdentityFieldsBody {
  clearance?: ClearanceLevel;
  usPersonVerified?: boolean;
  usPersonVerifiedAt?: string | null;
  usPersonReverifyDue?: string | null;
  hipaaBaaStatus?: HipaaBaaStatus;
  hipaaBaaSignedAt?: string | null;
  hipaaBaaExpiresAt?: string | null;
}

/**
 * Update a user's regulatory identity fields — wires to F61's existing
 * PATCH /users/:id/identity-fields. Encapsulates the call so the Edit
 * dialog can dispatch profile + identity fields to their respective
 * endpoints with shared cache invalidation.
 *
 * Self-edit is forbidden by the server (separation-of-duties — admins
 * cannot modify their own clearance / US-Person / BAA state).
 */
export function useUpdateIdentityFields() {
  const qc = useQueryClient();
  return useMutation<ApiUser, Error, {
    userId: string;
    body: UpdateIdentityFieldsBody;
  }>({
    mutationFn: ({ userId, body }) =>
      apiClient.patch(`/users/${userId}/identity-fields`, body),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['users', userId] });
      // F61 emits IDENTITY_FIELDS_UPDATED — show in the Audit tab.
      qc.invalidateQueries({ queryKey: ['users', userId, 'audit'] });
    },
  });
}

/**
 * What can be done with this account, by me, right now — straight from the
 * server. Backed by `GET /api/v1/users/:id/lifecycle`.
 *
 * Foundations 2 / B2a-T2. THE transition table lives in
 * `packages/shared/src/schemas/user-lifecycle.ts`; the API projects it through
 * `can()` and this hook hands the projection to the screen. The browser holds
 * NO map from status to actions: not which buttons a state shows, not what
 * they are called, not which ones prompt, not which ones need a reason, and
 * not why a missing one is missing.
 *
 * `refused` is as load-bearing as `allowed`. An action the caller cannot take
 * renders disabled with the server's `reason` as its tooltip, because an
 * operator who cannot see the action cannot find out why it is unavailable —
 * the failure mode the invited-user tooltip was hand-written to paper over.
 *
 * The shape is the shared `UserLifecycleResponse`, so a field added to the
 * table reaches the screen without a type written twice.
 */
export function useUserLifecycle(
  userId: string | undefined,
  /**
   * B2c-T3 — `enabled: false` holds the request until the surface actually
   * needs the answer. The roster asks per ROW, and a roster page carries up to
   * 500 of them: firing one request per row on paint would answer a question
   * nobody asked and turn opening the Users tab into 500 round trips. The row
   * asks when its menu opens; the detail page asks on paint, which is what it
   * has always done.
   */
  options: { enabled?: boolean } = {}
): {
  lifecycle: UserLifecycleResponse | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery<UserLifecycleResponse>({
    queryKey: ['users', userId, 'lifecycle'],
    queryFn: () =>
      apiClient.get<UserLifecycleResponse>(`/users/${userId}/lifecycle`),
    enabled: !!userId && (options.enabled ?? true),
    staleTime: 15_000,
  });
  return { lifecycle: data, isLoading, error };
}

/**
 * Update a user's status — wires to W1a-13's PATCH /users/:id/status.
 * Stage 2's W1a-13 lands the backend; this hook is wired in W1a-02 and
 * the lifecycle action buttons.
 *
 * B2a-T2: the destination type is the shared table's own status-surface
 * destination union, not a hand-copied string union, so a destination added
 * to the table cannot be un-sendable from here. A refused transition answers
 * 409 `INVALID_TRANSITION` carrying the table's `reason`, which the caller
 * surfaces verbatim.
 */
export function useUpdateUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, to, reason }: {
      userId: string;
      to: LifecycleStatusDestination;
      reason?: string;
    }) => {
      return apiClient.patch(`/users/${userId}/status`, { to, reason });
    },
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['users', userId] });
      // The set of legal next transitions changes with the state, so the
      // server's answer to "what can be done now" is stale the moment one
      // lands.
      qc.invalidateQueries({ queryKey: ['users', userId, 'lifecycle'] });
      // Bump activity / audit / sessions feeds for the affected user —
      // a status change emits a USER_STATUS_CHANGED audit row + (for
      // lock/suspend/deactivate) revokes refresh tokens.
      qc.invalidateQueries({ queryKey: ['users', userId, 'activity'] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'audit'] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'sessions'] });
    },
  });
}

/**
 * Remove one person from this organization — `DELETE /api/v1/users/:id`.
 *
 * B2c-T3. This is the `delete` SURFACE of the same transition table the status
 * controls read: the server returns the edge (label, glyph, emphasis, the
 * warning naming what cascades) in `GET /users/:id/lifecycle`, and pressing it
 * calls this. It is the SAME `deleteUser` cascade the roster's bulk delete
 * walks (B2c-T1) — role assignments, group memberships, department
 * memberships and every session go with the membership — so there is one
 * removal, reachable from the two places an operator looks for it.
 *
 * A refusal answers 409 `INVALID_TRANSITION` with the table's own sentence, or
 * 403 with the permission the edge costs; the caller renders it verbatim.
 */
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, { userId: string }>({
    mutationFn: ({ userId }) =>
      apiClient.delete<{ success: boolean }>(`/users/${userId}`),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['users', userId] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'lifecycle'] });
    },
  });
}

// ─── W1a-02 — User detail page hooks ────────────────────────────────────────

/**
 * B2e-T1 — THE vocabulary's own type, not a third copy of it.
 *
 * This union, the API's two response schemas and the API service each carried
 * their own hand-written copy, and all four carried `mfa_challenge`,
 * `mfa_enabled` and `mfa_disabled` — three verbs no emitter has ever written
 * a row for. Re-exported under the name the pages already import so the
 * change is one declaration, not a rename across the rail.
 */
export type UserActivityEventType = SharedUserActivityEventType;

export interface UserActivityEvent {
  id: string;
  timestamp: string;
  type: UserActivityEventType;
  ip: string | null;
  userAgent: string | null;
  location: string | null;
  outcome: 'success' | 'failure';
  details: string | null;
}

export interface UserAuditEvent {
  id: string;
  timestamp: string;
  action: string;
  category: string | null;
  outcome: string | null;
  resourceType: string;
  resourceId: string | null;
  actorUserId: string | null;
  actorEmail: string | null;
  details: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  recordHash: string | null;
  previousHash: string | null;
  sequenceNumber: string | null;
}

export interface UserSessionRow {
  id: string;
  device: string;
  deviceType: 'desktop' | 'mobile' | 'tablet';
  ip: string;
  location: string | null;
  startedAt: string;
  lastActiveAt: string;
  current: boolean;
  /**
   * B2d-T2 — a row is either the person's own sign-in or somebody
   * impersonating them right now. The tab draws the second differently; it
   * never hides it.
   */
  kind: 'sign_in' | 'impersonation';
  /** The impersonator's email on an `impersonation` row; null on a sign-in. */
  startedBy: string | null;
  /** When an impersonation session runs out; null on a sign-in. */
  expiresAt: string | null;
}

/**
 * Start an impersonation session — `POST /api/v1/users/:id/impersonate`
 * (B2d-T2).
 *
 * It replaces the old admin-prefixed start path, which was never mounted:
 * every call 404'd, and the Security tab's fail-closed catch drew "Access
 * Restricted" over the failure. The gate is `impersonate:users`; the refusals
 * about the subject (an Owner, yourself, somebody not live) are the server's
 * and arrive as 403 with the reason to show.
 */
export function useStartImpersonation() {
  const qc = useQueryClient();
  return useMutation<
    ImpersonationStart,
    Error,
    { userId: string; reason: string; durationMinutes?: number }
  >({
    mutationFn: ({ userId, reason, durationMinutes }) =>
      apiClient.post<ImpersonationStart>(`/users/${userId}/impersonate`, {
        reason,
        ...(durationMinutes ? { durationMinutes } : {}),
      }),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: ['users', userId, 'sessions'] });
      qc.invalidateQueries({ queryKey: ['impersonation'] });
    },
  });
}

/**
 * End somebody else's impersonation session — `POST
 * /users/impersonation/sessions/:id/revoke` (B2d-T2).
 *
 * The Sessions tab lists impersonation rows beside sign-ins, and its Revoke
 * control has to reach the right door: `DELETE /users/:id/sessions/:sid`
 * deletes a REFRESH TOKEN and would answer 404 for a session id, which is the
 * shape of lying button this rail keeps removing.
 */
export function useRevokeImpersonationSession() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, { userId: string; sessionId: string }>({
    mutationFn: ({ sessionId }) =>
      apiClient.post<{ success: boolean }>(
        `/users/impersonation/sessions/${sessionId}/revoke`,
      ),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: ['users', userId, 'sessions'] });
      qc.invalidateQueries({ queryKey: ['impersonation'] });
    },
  });
}

/** What `POST /users/:id/impersonate` answers with. */
export interface ImpersonationStart {
  sessionId: string;
  accessToken: string;
  expiresAt: string;
  targetUser: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

/**
 * Fetch the auth/session-class activity feed for a user. Backed by
 * `GET /api/v1/users/:id/activity`. Empty results return `data: []`,
 * never 404 (the user-existence check happens upstream and surfaces
 * as a hook-level error).
 */
export function useUserActivity(
  userId: string | undefined,
  page = 1,
  pageSize = 20
): {
  events: UserActivityEvent[];
  pagination: PaginatedResponse<UserActivityEvent>['pagination'] | null;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery<PaginatedResponse<UserActivityEvent>>({
    queryKey: ['users', userId, 'activity', page, pageSize],
    queryFn: () =>
      apiClient.get<PaginatedResponse<UserActivityEvent>>(
        `/users/${userId}/activity?page=${page}&pageSize=${pageSize}`
      ),
    enabled: !!userId,
    staleTime: 30_000,
  });
  return {
    events: data?.data ?? [],
    pagination: data?.pagination ?? null,
    isLoading,
    error,
  };
}

/**
 * Fetch the full audit feed for a user (every row where this user is
 * actor OR resource). Backed by `GET /api/v1/users/:id/audit`.
 */
export function useUserAudit(
  userId: string | undefined,
  page = 1,
  pageSize = 20
): {
  events: UserAuditEvent[];
  pagination: PaginatedResponse<UserAuditEvent>['pagination'] | null;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery<PaginatedResponse<UserAuditEvent>>({
    queryKey: ['users', userId, 'audit', page, pageSize],
    queryFn: () =>
      apiClient.get<PaginatedResponse<UserAuditEvent>>(
        `/users/${userId}/audit?page=${page}&pageSize=${pageSize}`
      ),
    enabled: !!userId,
    staleTime: 30_000,
  });
  return {
    events: data?.data ?? [],
    pagination: data?.pagination ?? null,
    isLoading,
    error,
  };
}

// ─── W1a-11 — Tenant-wide activity feed ──────────────────────────────────────

/**
 * Tenant-wide identity activity row — same per-row shape as
 * `UserActivityEvent` (per-user) but with the actor's identity
 * denormalized so the cross-user list UI can render "who did this"
 * without an extra lookup. The route is admin-only (`read:audit`).
 */
export interface TenantActivityRow {
  id: string;
  timestamp: string;
  userId: string;
  userEmail: string;
  userName: string | null;
  type: UserActivityEventType;
  ip: string | null;
  userAgent: string | null;
  location: string | null;
  outcome: 'success' | 'failure';
  details: string | null;
}

/**
 * Filters supported by the W1a-11 tenant-wide activity endpoint. All
 * keys are optional — omit to remove the filter. Date strings must be
 * full ISO-8601 (the server applies Zod `.datetime()`).
 */
export interface UseTenantActivityFilters {
  page?: number;
  pageSize?: number;
  /** Restrict to one actor's events. */
  userId?: string;
  /**
   * UI-friendly verb collapsing related actions.
   *
   * B2e-T1 — the vocabulary's filter alphabet. The copy this replaces offered
   * `session_terminated`, an action no emitter writes, so choosing it emptied
   * the feed and looked like a quiet tenant.
   */
  type?: UserActivityFilterType;
  /** Filter by audit-row outcome column. */
  outcome?: 'success' | 'failure';
  /** Inclusive ISO lower bound. */
  fromDate?: string;
  /** Inclusive ISO upper bound. */
  toDate?: string;
}

/**
 * Fetch the tenant-wide identity activity feed (W1a-11). Backed by
 * `GET /api/v1/users/activity`. Permission `read:audit` (admin/owner).
 *
 * Filters compose into a query string; keys with undefined values are
 * dropped before serialization so the URL stays clean for the common
 * "no filters" case.
 *
 * Returns `{ events, pagination, isLoading, error }` — symmetric with
 * `useUserActivity` so callers can swap between the two hooks.
 */
export function useTenantActivity(filters: UseTenantActivityFilters = {}): {
  events: TenantActivityRow[];
  pagination: PaginatedResponse<TenantActivityRow>['pagination'] | null;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  if (filters.userId) params.set('userId', filters.userId);
  if (filters.type) params.set('type', filters.type);
  if (filters.outcome) params.set('outcome', filters.outcome);
  if (filters.fromDate) params.set('fromDate', filters.fromDate);
  if (filters.toDate) params.set('toDate', filters.toDate);

  const qs = params.toString();
  const url = qs ? `/users/activity?${qs}` : '/users/activity';

  const { data, isLoading, error, refetch } = useQuery<
    PaginatedResponse<TenantActivityRow>
  >({
    queryKey: ['users', 'tenant-activity', filters],
    queryFn: () =>
      apiClient.get<PaginatedResponse<TenantActivityRow>>(url),
    staleTime: 15_000,
  });

  return {
    events: data?.data ?? [],
    pagination: data?.pagination ?? null,
    isLoading,
    error,
    refetch: () => {
      void refetch();
    },
  };
}

/**
 * The four numbers the Activity screen's cards render — counted by the SERVER
 * over the same audit rows the feed lists.
 *
 * B2e-T1. The page used to accumulate all four in a `for` loop over whichever
 * page of the feed it had loaded, and label them "(24h)". On a busy tenant
 * that window was an hour; on a quiet one, a month. Backed by
 * `GET /api/v1/users/activity/stats`, same `read:audit` permission as the
 * feed, and guarded by `enabled` so a member never burns a 403 on it.
 */
export function useTenantActivityStats(
  args: { window?: UserActivityStatWindow; enabled?: boolean } = {},
): {
  stats: UserActivityStats | null;
  isLoading: boolean;
  error: unknown;
} {
  const window = args.window ?? '24h';
  const enabled = args.enabled !== false;
  const { data, isLoading, error } = useQuery<UserActivityStats>({
    queryKey: ['users', 'tenant-activity-stats', window],
    queryFn: () =>
      apiClient.get<UserActivityStats>(
        `/users/activity/stats?window=${encodeURIComponent(window)}`,
      ),
    enabled,
    staleTime: 15_000,
  });

  return { stats: data ?? null, isLoading, error };
}

/** Re-exported so the screens read the alphabet and the card->filter map from
 * the one vocabulary rather than listing either again. */
export { USER_ACTIVITY_FILTER_TYPES, USER_ACTIVITY_STAT_TYPES };
export type { UserActivityFilterType, UserActivityStats, UserActivityStatWindow };

/**
 * Fetch the active session list for a user. Backed by
 * `GET /api/v1/users/:id/sessions`.
 */
export function useUserSessions(userId: string | undefined): {
  sessions: UserSessionRow[];
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery<{ data: UserSessionRow[] }>({
    queryKey: ['users', userId, 'sessions'],
    queryFn: () =>
      apiClient.get<{ data: UserSessionRow[] }>(`/users/${userId}/sessions`),
    enabled: !!userId,
    staleTime: 15_000,
  });
  return {
    sessions: data?.data ?? [],
    isLoading,
    error,
  };
}

/**
 * Revoke a single session (refresh-token row). Wires to
 * `DELETE /api/v1/users/:id/sessions/:sid`. Self-revoke is always
 * allowed; cross-user revoke requires `lifecycle:lock:users`.
 */
export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, sessionId }: {
      userId: string;
      sessionId: string;
    }) => {
      return apiClient.delete<{ success: boolean }>(
        `/users/${userId}/sessions/${sessionId}`
      );
    },
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: ['users', userId, 'sessions'] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'activity'] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'audit'] });
    },
  });
}

// ─── W1a-12 — Bulk user actions ─────────────────────────────────────────────

/**
 * Shared response shape for the three mutation bulk endpoints
 * (reset-mfa / force-password-reset / access-review).
 *
 * `succeeded` carries the userIds that were actually mutated. `skipped`
 * carries the per-id reasons for non-application:
 *   - 'self'         caller's own id was in the request body; bulk
 *                    operations skip self silently rather than 403'ing
 *                    the whole batch (separation-of-duties; the caller
 *                    can use the per-user self-service flow)
 *   - 'cross_tenant' id belongs to a different tenant (or doesn't exist —
 *                    the API treats both as the same skip class to avoid
 *                    leaking enumeration information)
 *   - 'not_found'    id doesn't exist or is purge-eligible (deletedAt < NOW)
 *
 * `totalRequested` is the original `userIds.length` so the UI can render
 * "5 of 8 succeeded" without re-counting.
 */
export interface BulkResult {
  totalRequested: number;
  succeeded: string[];
  skipped: Array<{
    userId: string;
    reason: 'self' | 'cross_tenant' | 'not_found';
  }>;
}

/** Body shape for the three mutation bulk endpoints. */
export interface BulkUserActionBody {
  userIds: string[];
  /** Optional reason — surfaces in the audit row's `details.reason`. */
  reason?: string;
}

/**
 * Bulk reset MFA — wires to W1a-12's POST /users/bulk/reset-mfa.
 *
 * Permission: `mfa:reset:users` (admin/owner). For each in-tenant target
 * (excluding self), clears mfa_enabled + mfa_secret and revokes all
 * refresh tokens. Emits a single batch audit row + per-user records.
 */
export function useBulkResetMfa() {
  const qc = useQueryClient();
  return useMutation<BulkResult, Error, BulkUserActionBody>({
    mutationFn: (body) =>
      apiClient.post<BulkResult>('/users/bulk/reset-mfa', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * Bulk force-password-reset — POST /users/bulk/force-password-reset.
 *
 * Permission: `mfa:reset:users` (same admin+ tier as reset-mfa — both are
 * credential-hygiene actions).
 *
 * B2d-T1 — IT NOW DOES SOMETHING. The endpoint answered an honest 501 while
 * nothing read `password_must_change`; `routes/v1/auth.ts` reads it at every
 * door that mints a token, so the action raises the flag, ends every session
 * the old credential opened, and the next login can only change the password.
 */
export function useBulkForcePasswordReset() {
  const qc = useQueryClient();
  return useMutation<BulkResult, Error, BulkUserActionBody>({
    mutationFn: (body) =>
      apiClient.post<BulkResult>('/users/bulk/force-password-reset', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * Force password reset for ONE person — POST /users/:id/force-password-reset.
 *
 * The user-detail door onto the same server-side code path the bulk action
 * walks (`applyForcePasswordReset`), so the two surfaces cannot come to mean
 * different things by "force password reset". Permission: `mfa:reset:users`.
 */
export function useForcePasswordReset() {
  const qc = useQueryClient();
  return useMutation<
    { success: boolean },
    Error,
    { userId: string; reason?: string }
  >({
    mutationFn: ({ userId, reason }) =>
      apiClient.post<{ success: boolean }>(
        `/users/${userId}/force-password-reset`,
        reason ? { reason } : {},
      ),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['users', userId] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'activity'] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'sessions'] });
    },
  });
}

/**
 * Bulk start-access-review — wires to W1a-12's
 * POST /users/bulk/access-review.
 *
 * Permission: `write:users` (admin+). For each in-tenant target
 * (excluding self), sets users.access_review_started_at=NOW(). The
 * actual review workflow (Trust module Wave 6, F-W1a-12.2) consumes the
 * timestamp and clears it via access_review_completed_at.
 */
export function useBulkAccessReview() {
  const qc = useQueryClient();
  return useMutation<BulkResult, Error, BulkUserActionBody>({
    mutationFn: (body) =>
      apiClient.post<BulkResult>('/users/bulk/access-review', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/** Body shape for the bulk users-CSV export endpoint. */
export interface BulkExportBody {
  /** Explicit subset of users to export (uuid[]). */
  userIds?: string[];
  /** Filter object — same shape as the user-list query string. */
  filters?: {
    status?: string;
    role?: string;
    search?: string;
  };
  /** Field selection (all default). */
  fields?: Array<
    | 'email'
    | 'firstName'
    | 'lastName'
    | 'role'
    | 'status'
    | 'createdAt'
    | 'lastLoginAt'
  >;
  /** Currently always 'csv'; xlsx/json deferred. */
  format?: 'csv';
}

/**
 * Bulk users CSV export — wires to W1a-12's POST /users/bulk/export.
 *
 * Permission: `export:users` (admin+). The endpoint returns `text/csv`
 * (not JSON), so this hook bypasses the apiClient JSON parser and uses
 * a raw fetch to capture the response body as a Blob. The caller is
 * responsible for triggering the browser download (e.g. by creating an
 * object URL + clicking a hidden anchor).
 *
 * Returns `{ url, blob, filename }` — `url` is an in-memory Blob URL the
 * caller can attach to an `<a download={filename} href={url}>` to drive
 * the download. The caller MUST `URL.revokeObjectURL(url)` after the
 * click to free the blob (the hook does NOT auto-revoke because it
 * doesn't own the navigation lifecycle).
 */
export function useBulkExportUsers() {
  return useMutation<
    { url: string; blob: Blob; filename: string },
    Error,
    BulkExportBody
  >({
    mutationFn: async (body) => {
      const accessToken = localStorage.getItem('accessToken');
      const res = await fetch('/api/v1/users/bulk/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        // Drain a JSON error body if present (the bulk-export endpoint
        // emits JSON errors for 4xx; only 200 returns text/csv).
        let message = `Export failed: ${res.status}`;
        try {
          const err = await res.json();
          message = err?.error?.message ?? message;
        } catch {
          // Non-JSON 4xx (rare) — fall through to status-based message.
        }
        const error = new Error(message);
        (error as Error & { status?: number }).status = res.status;
        throw error;
      }

      // Parse Content-Disposition for the server-suggested filename.
      // RFC 6266: `attachment; filename="users-{slug}-{date}.csv"`
      const disposition = res.headers.get('Content-Disposition') ?? '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : 'users.csv';

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      return { url, blob, filename };
    },
  });
}

// ─── Cluster C-STUB-TOAST — Bulk delete via the legacy /users/bulk path ─────

/**
 * Bulk soft-delete users — wires the Users-tab "Delete users" dropdown
 * item to the real backend endpoint at
 * `packages/api/src/routes/v1/users.ts:3734` (POST /users/bulk).
 *
 * Body shape is the v0 (lifecycle-bulk) contract:
 *   { action: 'activate' | 'deactivate' | 'delete', userIds: string[] }
 *
 * Per Phase 2 resolved decision #3 (founder-locked 2026-05-14) a stub
 * toast on a destructive action is a P0 defect. Before this hook landed
 * the dropdown fell into the "// stub toast so the UI doesn't break"
 * branch and the operator saw `Deleted (stub) — would apply to N users`
 * with NO mutation. This hook closes that gap.
 *
 * Permission: requireAdmin — the endpoint scopes target users to the
 * caller's organisation, so cross-tenant ids silently no-op rather than
 * leaking. The response `affected` count reflects only in-tenant
 * matches that were actually flipped (we surface it via the toast).
 *
 * Note: this routes through the LEGACY `/bulk` action='delete' path
 * pending F-W1a-12.1 (state-machine bulk delete). When that ships the
 * caller signature stays the same — only the underlying URL changes.
 */
/**
 * B2c-T1 — PER-ROW RESULTS, not a count. `POST /users/bulk` answered
 * `{ success, affected }`, which could not say which of the ids were
 * cross-tenant, which were the caller themselves and which were in a state the
 * action cannot leave. Every id now comes back with its own outcome and, when
 * it was not applied, the server's own sentence.
 *
 * B2c-T3 renders these per row in the results panel; this type and the toast
 * below are what keeps the action honest in the meantime.
 */
export interface BulkRowResult {
  id: string;
  // B2c-T1 fix (review R-06) — `failed` mirrors the fourth outcome the endpoint
  // can now answer: allowed and declared, but the write itself did not land.
  // The endpoint used to rethrow that case as a 500 with no `results` at all.
  outcome: 'applied' | 'refused' | 'skipped' | 'failed';
  reason?: string;
}

export interface BulkDeleteResult {
  success: boolean;
  results: BulkRowResult[];
}

/**
 * WHAT THE BATCH DID, as ONE answer — the severity and the sentence together.
 *
 * B2c-T1 fix (review R-07). Both bulk surfaces were deciding this for
 * themselves and both got it wrong in the same direction: a destructive action
 * in which the server refused EVERY row still rendered a green success toast
 * ("Deleted 0 of 3 users"), which tells the operator the delete went through.
 * The severity is a property of the outcome, not of the page, so it is decided
 * here, once, and the pages render what they are handed.
 *
 * @param verb  the past-tense verb for the action ('Deleted', 'Deactivated').
 */
export function summarizeBulkRows(
  rows: readonly BulkRowResult[],
  requested: number,
  verb: string
): { severity: 'success' | 'warning' | 'error'; message: string } {
  const applied = rows.filter((r) => r.outcome === 'applied').length;
  // The server's own sentence for the first row it did not apply. B2c-T3 moves
  // every row into the results panel; until then the operator gets the reason
  // rather than a count they cannot act on.
  const first = rows.find((r) => r.outcome !== 'applied');
  const because = first?.reason ? ` — ${first.reason}` : '';

  if (requested > 0 && applied === requested) {
    return {
      severity: 'success',
      message: `${verb} ${applied} user${applied === 1 ? '' : 's'}`,
    };
  }
  if (applied === 0) {
    return {
      severity: 'error',
      message: `No users were ${verb.toLowerCase()} (0 of ${requested})${because}`,
    };
  }
  return {
    severity: 'warning',
    message: `${verb} ${applied} of ${requested} users${because}`,
  };
}

/**
 * ONE ROW PER ID, whatever the endpoint answered.
 *
 * B2c-T3 renders the outcome of a bulk action per row. Three shapes reach that
 * panel and they are normalised here, once:
 *
 *   - `POST /users/bulk` already answers `{ id, outcome, reason? }` per id
 *     (B2c-T1) — it needs no conversion;
 *   - the per-id lifecycle loop (lock / unlock / suspend / activate) answers
 *     one settled promise per id, and a rejected one carries the server's own
 *     refusal sentence — `bulkRowFromRejection` lifts it out;
 *   - the W1a-12 batch endpoints (reset MFA, force password reset, access
 *     review) answer `{ succeeded[], skipped[] }` with a CODE per skip —
 *     `bulkRowsFromSkipResult` turns that into rows.
 */
/**
 * Did the SERVER decide this row, or did the write never land?
 *
 * B2c-T3 fix (review B2cT3-R1 / BF-03). Every rejection used to be stamped
 * `refused`, which tells the operator the platform considered the change and
 * declined it. A restarting api container rejects twelve `PATCH
 * /users/:id/status` calls with `TypeError: Failed to fetch` — nothing was
 * decided, nothing was locked, and the next move is "retry", not "go and look
 * at your entitlements". The union has carried `failed` for exactly this case
 * since B2c-T1; this is the reader that can produce it.
 *
 * A DECISION is a 4xx the route answered: 409 INVALID_TRANSITION with the
 * table's sentence, 403 with the key the edge costs, 404 for somebody who is
 * not a member here. A 401 (the session went), a 408/429 (never handled), a
 * 5xx and a rejection with no status at all are not decisions — the request
 * did not reach a verdict, and some of them may even have landed server-side.
 * Unknown shapes fall to `failed`: claiming the platform refused something it
 * may have applied is the fail-OPEN reading (principles.md #4).
 */
function rejectionWasServerDecision(err: unknown): boolean {
  // The one reader for "this is the permission boundary" — it also catches the
  // shape that carries `FORBIDDEN` with no numeric status.
  if (isPermissionDeniedApiError(err)) return true;
  const status = (err as ApiClientError | undefined)?.status;
  if (typeof status !== 'number') return false;
  if (status === 401 || status === 408 || status === 429) return false;
  return status >= 400 && status < 500;
}

export function bulkRowFromRejection(id: string, err: unknown): BulkRowResult {
  const decided = rejectionWasServerDecision(err);
  return {
    id,
    outcome: decided ? 'refused' : 'failed',
    // The server's own sentence: a refused transition answers 409
    // INVALID_TRANSITION carrying the table's `reason`, a permission refusal
    // answers 403 with the key the edge costs. Rendered verbatim — the browser
    // writes no refusal of its own. A row that never reached a verdict carries
    // whatever the transport said, under a fallback that says so.
    reason: getApiErrorMessage(
      err,
      decided
        ? 'The server refused this change.'
        : 'The change did not reach the server — nothing was applied for this person.'
    ),
  };
}

/**
 * The sentence for a skip CODE the W1a-12 batch endpoints answer with.
 *
 * Those three endpoints return a code, not prose (unlike `POST /users/bulk`,
 * which returns the table's sentence). Naming the code is presentation — the
 * same kind of binding as an icon key — so it lives in one place and an
 * unrecognised code still renders its row, carrying the raw code, because a
 * row nobody can explain must still be a row the operator can see.
 */
const BULK_SKIP_SENTENCES: Record<string, string> = {
  self: 'Your own account — bulk actions skip you.',
  cross_tenant: 'Not a member of this organization.',
  not_found: 'No such user in this organization.',
};

export function bulkRowsFromSkipResult(result: {
  succeeded: string[];
  skipped: Array<{ userId: string; reason: string }>;
}): BulkRowResult[] {
  return [
    ...result.succeeded.map<BulkRowResult>((id) => ({ id, outcome: 'applied' })),
    ...result.skipped.map<BulkRowResult>((s) => ({
      id: s.userId,
      outcome: 'skipped',
      reason: BULK_SKIP_SENTENCES[s.reason] ?? s.reason,
    })),
  ];
}

/**
 * The permission keys that could pay for an edge INTO this state, read off the
 * transition table.
 *
 * B2c-T3. The roster's bulk bar used to gate its buttons on hand-written
 * guesses — Activate on `write:users`, Deactivate on `delete:users`, Lock on
 * `lifecycle:lock:users` — a second copy of the table's `permission` field,
 * and already wrong: founder decision 12 prices deactivation `write:users`, so
 * the Deactivate button was hidden from exactly the administrators who are
 * allowed to press it.
 *
 * The price depends on the (from, to) pair and a selection holds many `from`s,
 * so the honest client-side question is "could this caller walk ANY edge into
 * this state?" — every row is then decided by the server, per row, and the
 * refusals come back in the results panel with the table's own sentences.
 */
export function lifecycleDestinationPermissions(
  to: LifecycleState
): readonly RbacPermission[] {
  const keys = new Set<RbacPermission>();
  for (const from of LIFECYCLE_STATES) {
    for (const transition of LIFECYCLE_TRANSITIONS[from]) {
      if (transition.to !== to) continue;
      // ADMINISTRATOR EDGES ONLY (review B2cT3-R5 / BF-06). The buttons this
      // prices are an administrator's, and the table declares edges no
      // administrator can walk: `invited → active` is `actor: 'subject'` and
      // `decideTransition` refuses it with `subject-only` before the permission
      // is ever weighed. Counting its key would let an edge the caller can
      // never walk pay for a button — a batch of rows that all come back
      // refused, which is the dead button this rail keeps deleting.
      if (transition.actor !== 'administrator') continue;
      keys.add(transition.permission);
    }
  }
  return [...keys];
}

export function useBulkDeleteUsers() {
  const qc = useQueryClient();
  return useMutation<BulkDeleteResult, Error, { userIds: string[] }>({
    mutationFn: (body) =>
      apiClient.post<BulkDeleteResult>('/users/bulk', {
        action: 'delete',
        userIds: body.userIds,
      }),
    onSuccess: () => {
      // A bulk delete removes each person from THIS tenant (their membership
      // reads `deleted`, B2c-T1) — the user list must refetch so the removed
      // rows disappear from the table.
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// ─── W1a-14 — Bulk users CSV import (multipart upload + dry-run) ───────────

/**
 * Per-row error entry from the import endpoint.
 *
 * `row` is 1-based to match what an operator would see in their CSV
 * editor. `email` is the lowercased value extracted from the row (or ''
 * when the email cell itself was missing). `code` is one of the
 * documented error codes (MISSING_EMAIL, INVALID_EMAIL, INVALID_ROLE,
 * EMAIL_EXISTS_IN_TENANT, EMAIL_EXISTS_IN_OTHER_TENANT, ROLE_EXCEEDS_ACTOR,
 * DUPLICATE_EMAIL_IN_FILE, MISSING_FIRST_NAME, MISSING_LAST_NAME).
 */
export interface ImportRowError {
  row: number;
  email: string;
  code: string;
  message: string;
}

/**
 * Per-row success entry from the import endpoint.
 *
 * Populated only on a real (non-dryRun) run with zero validation errors.
 * `inviteUrl` is the one-shot raw-token-bearing URL the operator can copy
 * for distribution in `manual_link` mode (the SHA-256 hash is persisted
 * server-side; the raw token is never returnable again).
 */
export interface ImportRowSuccess {
  row: number;
  email: string;
  userId: string;
  inviteUrl: string;
}

/**
 * Result wire-shape for `POST /users/bulk/import`.
 *
 * The endpoint always returns 200 (even if `invalidRows > 0`) so the UI
 * can render the same dialog whether the run is a dry-run validation
 * pass or a real import. `succeeded[]` is empty on dry-runs and on real
 * runs that fail-fast due to validation errors.
 */
export interface BulkImportResult {
  dryRun: boolean;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errors: ImportRowError[];
  succeeded: ImportRowSuccess[];
}

/**
 * Bulk users CSV import — wires to W1a-14's POST /users/bulk/import.
 *
 * Permission: `write:users` (admin/owner). Multipart upload with file
 * field `file` (CSV, ≤5 MB, ≤1000 rows) plus optional form fields
 * `dryRun` ('true'|'false', default 'false') and `inviteMode`
 * ('send_now'|'manual_link', default 'manual_link').
 *
 * Semantics:
 *   - dryRun=true → returns validation report; emits BULK_USERS_IMPORTED_DRYRUN audit
 *   - dryRun=false:
 *       - if ANY row is invalid → returns the same shape with succeeded:[]
 *         (real run is fail-fast: NO partial commits)
 *       - all rows valid → atomic transaction inserts all rows + memberships
 *         + invitations + per-row USER_CREATED audit + single batch
 *         BULK_USERS_IMPORTED audit (head-of-chain).
 *
 * The hook bypasses apiClient (which sets Content-Type: application/json)
 * because multipart requires the browser to set the boundary itself.
 *
 * Cache invalidation: only invalidates `users` and `invitations` lists
 * when the real run actually committed at least one row.
 */
export function useBulkImportUsers() {
  const qc = useQueryClient();
  return useMutation<
    BulkImportResult,
    Error,
    { file: File; dryRun: boolean; inviteMode?: 'send_now' | 'manual_link' }
  >({
    mutationFn: async ({ file, dryRun, inviteMode }) => {
      const accessToken = localStorage.getItem('accessToken');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dryRun', String(dryRun));
      if (inviteMode) {
        fd.append('inviteMode', inviteMode);
      }
      const res = await fetch('/api/v1/users/bulk/import', {
        method: 'POST',
        headers: {
          // NB: do NOT set Content-Type — the browser sets the
          // multipart boundary automatically when given a FormData body.
          ...(accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : {}),
        },
        body: fd,
      });
      if (!res.ok) {
        let message = `Import failed: ${res.status}`;
        try {
          const e = await res.json();
          message = e?.error?.message ?? message;
        } catch {
          // Non-JSON 4xx/5xx — fall through to status-based message.
        }
        const error = new Error(message);
        (error as Error & { status?: number }).status = res.status;
        throw error;
      }
      return (await res.json()) as BulkImportResult;
    },
    onSuccess: (data) => {
      // Only invalidate user lists when the real run committed inserts.
      if (!data.dryRun && data.succeeded.length > 0) {
        qc.invalidateQueries({ queryKey: ['users'] });
        qc.invalidateQueries({ queryKey: ['invitations'] });
      }
    },
  });
}


// ─── Re-exports from split sub-hooks (issue #134) ───────────────────────────
//
// Per-domain hooks were extracted into focused files to keep this module
// under control. The public API stays unchanged — all 16 consumers across
// `routes/users/*` and `components/users/*` continue to import from
// `lib/use-users`. New code is welcome to import from the per-domain file
// directly for tighter dependency boundaries.

export * from './use-roles';
export * from './use-departments';
export * from './use-user-groups';
export * from './use-invitations';
export * from './use-sso-connections';
